import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import clsx from "clsx"

type Props = {
  content: React.ReactNode
  children: React.ReactNode
  className?: string
  side?: "top" | "bottom" | "left" | "right"
  width?: number
  offset?: number
}

export function Tooltip({ content, children, className, side = "top", width = 320, offset = 8 }: Props) {
  const anchorRef = useRef<HTMLSpanElement | null>(null)
  const tooltipRef = useRef<HTMLDivElement | null>(null)

  const [open, setOpen] = useState(false)
  const [mounted, setMounted] = useState(false)

  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)

  useEffect(() => setMounted(true), [])

  const computePosition = () => {
    const anchor = anchorRef.current
    const tip = tooltipRef.current
    if (!anchor || !tip) return

    const a = anchor.getBoundingClientRect()
    const t = tip.getBoundingClientRect()

    let top = 0
    let left = 0

    if (side === "top") {
      top = a.top - t.height - offset
      left = a.left + a.width / 2 - t.width / 2
    } else if (side === "bottom") {
      top = a.bottom + offset
      left = a.left + a.width / 2 - t.width / 2
    } else if (side === "left") {
      top = a.top + a.height / 2 - t.height / 2
      left = a.left - t.width - offset
    } else {
      top = a.top + a.height / 2 - t.height / 2
      left = a.right + offset
    }

    const pad = 8
    top = Math.max(pad, Math.min(top, window.innerHeight - t.height - pad))
    left = Math.max(pad, Math.min(left, window.innerWidth - t.width - pad))

    setPos({ top, left })
  }

  useLayoutEffect(() => {
    if (!open) return
    computePosition()

    const onScroll = () => computePosition()
    const onResize = () => computePosition()

    window.addEventListener("scroll", onScroll, true)
    window.addEventListener("resize", onResize)

    return () => {
      window.removeEventListener("scroll", onScroll, true)
      window.removeEventListener("resize", onResize)
    }
  }, [open, side, offset])

  let closeTimer: any = null
  const onEnter = () => {
    if (closeTimer) clearTimeout(closeTimer)
    setOpen(true)
  }
  const onLeave = () => {
    closeTimer = setTimeout(() => setOpen(false), 80)
  }

  return (
    <span ref={anchorRef} className={clsx("inline-flex", className)} onMouseEnter={onEnter} onMouseLeave={onLeave}>
      {children}

      {mounted && open
        ? createPortal(
            <div
              ref={tooltipRef}
              onMouseEnter={onEnter}
              onMouseLeave={onLeave}
              className={clsx(
                "fixed z-[999999] pointer-events-auto",
                "rounded-lg border border-[rgba(255,255,255,0.12)] bg-[#0C0C1A] text-white shadow-lg",
                "px-3 py-2 text-xs leading-5"
              )}
              style={{
                width,
                top: pos?.top ?? -9999,
                left: pos?.left ?? -9999
              }}
            >
              {content}
            </div>,
            document.body
          )
        : null}
    </span>
  )
}
